// Single source of truth for the 4 high-level card-network dispute categories
// (Fraud / Authorization / Processing Errors / Consumer Disputes). Used by the
// per-category rules dropdown (PreferencesPage) and the disputes Category filter
// (AllPage) to present the fine-grained reason categories grouped/rolled-up into
// the 4 buckets internal operators recognize.
//
// IMPORTANT: the fine-grained `reason_category` (the 14 claim types) is NOT
// collapsed. It still drives evidence-point selection + analytics. These groups
// are a presentation / rollup layer only.

export const CATEGORY_GROUP_ORDER = [
  "fraud",
  "consumer_disputes",
  "processing_errors",
  "authorization"
]

export const CATEGORY_GROUP_LABELS = {
  fraud: "Fraud (Card Absent + Card Present)",
  consumer_disputes: "Consumer Disputes Category",
  processing_errors: "Processing Errors Category",
  authorization: "Authorization-related Chargeback"
}

// The same four groups, named for a chart legend. The long labels above spell out the
// network taxonomy for a filter dropdown; a donut slice or an axis tick has no room for it.
export const CATEGORY_GROUP_SHORT_LABELS = {
  fraud: "Fraud",
  authorization: "Authorization",
  processing_errors: "Processing error",
  consumer_disputes: "Consumer dispute"
}

export const CATEGORY_TO_GROUP = {
  fraudulent: "fraud",
  unrecognized: "fraud",
  product_not_received: "consumer_disputes",
  product_unacceptable: "consumer_disputes",
  subscription_canceled: "consumer_disputes",
  credit_not_processed: "consumer_disputes",
  customer_initiated: "consumer_disputes",
  duplicate: "processing_errors",
  incorrect_account_details: "processing_errors",
  check_returned: "processing_errors",
  general: "processing_errors",
  debit_not_authorized: "authorization",
  insufficient_funds: "authorization",
  bank_cannot_process: "authorization"
}

// Fold a flat [{value,label,disabled}] option list into antd Select option-groups
// keyed by the 4 network categories. Anything without a known group is kept flat at
// the end so an unmapped category is never dropped.
export const groupByNetworkCategory = (flat, t) => {
  const byGroup = {}
  const ungrouped = []
  flat.forEach((o) => {
    const g = CATEGORY_TO_GROUP[o.value]
    if (g) (byGroup[g] = byGroup[g] || []).push(o)
    else ungrouped.push(o)
  })
  const groups = CATEGORY_GROUP_ORDER.filter((g) => byGroup[g] && byGroup[g].length).map((g) => ({
    label: t(CATEGORY_GROUP_LABELS[g]),
    options: byGroup[g]
  }))
  return ungrouped.length ? groups.concat(ungrouped) : groups
}

// The 4 network groups as {id, name} options for the disputes Category filter.
export const networkGroupFilterData = (t) =>
  CATEGORY_GROUP_ORDER.map((g) => ({ id: g, name: t(CATEGORY_GROUP_LABELS[g]) }))

// Expand selected network-group ids back to the underlying fine-grained category
// slugs the backend filters on (reason_category__in). A value that is already a
// fine-grained slug (not a group id) is passed through unchanged, so explicit
// category values still work.
export const expandGroupsToCategories = (groupIds) => {
  if (!groupIds || !groupIds.length) return groupIds
  const out = []
  groupIds.forEach((g) => {
    if (CATEGORY_GROUP_LABELS[g]) {
      Object.keys(CATEGORY_TO_GROUP).forEach((cat) => {
        if (CATEGORY_TO_GROUP[cat] === g) out.push(cat)
      })
    } else {
      out.push(g)
    }
  })
  return out
}
